import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { renderTextWithLinks } from "@/shared/utils/helperFunctions";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";

const cn = (...inputs) => twMerge(clsx(inputs));

const TOOLTIP_Z_INDEX = 9999999999;
const ARROW_HEIGHT = 6;
const GAP = 8;

const CustomCommonTooltip = ({
  children,
  content,
  placement = "top",
  showArrow = true,
  className,
  delay = 200,
  closeDelay = 280,
}) => {
  const [open, setOpen] = React.useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    strategy: "fixed",
    middleware: [
      offset(ARROW_HEIGHT + GAP),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      ...(showArrow ? [arrow({ element: arrowRef, padding: 4 })] : []),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    move: false,
    delay: { open: delay, close: closeDelay },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context, { escapeKey: true });
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const tooltipContent = (
    <div
      ref={refs.setFloating}
      className={cn(
        "rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-[0.69rem] text-gray-800 dark:text-gray-100 shadow-lg border border-gray-200 dark:border-gray-700",
        "max-w-[280px] break-words whitespace-pre-line",
        "transition-opacity duration-150 ease-out",
        className,
      )}
      style={{
        ...floatingStyles,
        zIndex: TOOLTIP_Z_INDEX,
      }}
      {...getFloatingProps()}
    >
      {typeof content === "string" ? renderTextWithLinks(content) : content}

      {showArrow && (
        <FloatingArrow
          ref={arrowRef}
          context={context}
          height={ARROW_HEIGHT}
          width={ARROW_HEIGHT * 2}
          strokeWidth={1}
          className="fill-white dark:fill-gray-800 stroke-gray-200 dark:stroke-gray-700 [&>path:last-of-type]:stroke-white dark:[&>path:last-of-type]:stroke-gray-800"
        />
      )}
    </div>
  );

  const hasContent = content != null && content !== "";

  return (
    <>
      <div
        ref={refs.setReference}
        className="relative inline-flex"
        {...getReferenceProps()}
      >
        {children}
      </div>

      {hasContent &&
        open &&
        createPortal(tooltipContent, document.body, "custom-tooltip-portal")}
    </>
  );
};

export default CustomCommonTooltip;
